import {
  Breadcrumb as RACBreadcrumb,
  Breadcrumbs as RACBreadcrumbs,
  Link,
  type BreadcrumbProps,
  type BreadcrumbsProps,
  type LinkProps,
} from "react-aria-components";
import { MdChevronRight } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";

const trailStyles = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "xsmall",
  listStyle: "none",
  margin: 0,
  padding: 0,
  fontSize: "medium",
});

const crumbStyles = css({ display: "flex", alignItems: "center", gap: "xsmall", color: "text.secondary" });

const linkStyles = css({
  color: "fuchsia",
  textDecoration: "underline",
  cursor: "pointer",
  outline: "none",
  _focusVisible: { outline: "2px solid {colors.bootstrapBlue}" },
  "&[data-current]": { color: "text.secondary", textDecoration: "none", cursor: "default" },
});

/** A trail of links; the last Breadcrumb is the current page and isn't interactive. */
export function Breadcrumbs<T extends object>(props: Omit<BreadcrumbsProps<T>, "className">) {
  const { t } = useTranslation(["common"]);
  // React Aria renders just the list; the nav makes it a landmark.
  return (
    <nav aria-label={t(($) => $.common.breadcrumb.breadcrumbs)}>
      <RACBreadcrumbs {...props} className={trailStyles} />
    </nav>
  );
}

/** Takes Link props: give every crumb but the last an `href` or `onPress`. */
export function Breadcrumb({
  children,
  ...props
}: Omit<BreadcrumbProps, "className" | "children"> & Omit<LinkProps, "className">) {
  return (
    <RACBreadcrumb {...props} className={crumbStyles}>
      {({ isCurrent }) => (
        <>
          <Link {...props} className={linkStyles}>
            {children}
          </Link>
          {!isCurrent && <MdChevronRight aria-hidden />}
        </>
      )}
    </RACBreadcrumb>
  );
}
